import { useState } from 'react'
import { useNavigate } from 'react-router'

import { TablePagination } from '@/components/ui/pagination'
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table'
import { SkeletonRows } from '@/components/ui/skeleton'
import { type EntityKind, type EntitySummary, filePath, useVaultEntityPage } from '@/models/vault'
import { SlotStatusPill } from './SlotStatusPill'
import { useVault } from './VaultContext'
import { VaultSection } from './VaultSection'

// Ten rows keeps three tables on one screen; the rest are a page away.
const PAGE_SIZE = 10

// Fixed tracks on the two trailing columns, so Drivers and Equipment line up with
// each other instead of each sizing to its own longest name.
const COLUMNS: ResponsiveTableColumn<EntitySummary>[] = [
  {
    key: 'name',
    header: 'Name',
    cell: (entity) => <span className="truncate font-medium">{entity.name}</span>,
    hideOnCard: true,
  },
  {
    key: 'onFile',
    header: 'Documents',
    cell: (entity) => `${entity.onFileCount} of ${entity.requiredCount} on file`,
    headClassName: 'w-44',
    cellClassName: 'text-muted-foreground w-44 whitespace-nowrap',
  },
  {
    key: 'status',
    header: 'Status',
    cell: (entity) => <SlotStatusPill status={entity.status} rollup />,
    headClassName: 'w-32',
    cellClassName: 'w-32',
  },
]

/** Drivers, or trucks and trailers, listed where the folder used to be. */
export function VaultEntityTable({
  kind,
  title,
  empty,
  itemLabel,
}: {
  kind: EntityKind
  title: string
  empty: string
  itemLabel: string
}) {
  const { orgId, href } = useVault()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const { entities, total, hasNextPage, hasPreviousPage, isLoading } = useVaultEntityPage(
    orgId,
    kind,
    page,
    PAGE_SIZE
  )

  return (
    <VaultSection title={title} count={total} scope={kind}>
      {isLoading ? (
        <SkeletonRows />
      ) : entities.length === 0 ? (
        <p className="text-muted-foreground text-sm">{empty}</p>
      ) : (
        <ResponsiveTable
          items={entities}
          columns={COLUMNS}
          getRowId={(entity) => entity.id}
          cardTitle={(entity) => entity.name}
          onRowClick={(entity) =>
            void navigate(href(filePath(entity.kind, entity.id)), { preventScrollReset: true })
          }
        />
      )}
      <TablePagination
        page={page}
        count={total}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={setPage}
        itemLabel={itemLabel}
        className="py-3"
      />
    </VaultSection>
  )
}
